<template>
  <div class="lines-container">
    <!-- 线路列表 -->
    <custom-table
      ref="customTable"
      :customOptions="customOptions"
      :columns="columns"
      @selection-change="handleSelectionChange">
      <template slot="search-button-right">
        <el-button
          type="success"
          icon="el-icon-setting"
          @click="handleBatchSet"
          :disabled="selectedLines.length === 0">
          批量配置 ({{ selectedLines.length }})
        </el-button>
      </template>
      <template slot="table-operation">
        <el-table-column label="操作" width="120" fixed="right" align="center">
          <div slot-scope="{row}" class="custom-action">
            <el-button type="text" icon="el-icon-edit" @click="handleEdit(row)">配置</el-button>
          </div>
        </el-table-column>
      </template>

      <template slot-scope="{item, row}">
        <template v-if="item.prop === 'lineName'">
          <div class="line-name-cell">
            <span class="line-name">{{ row.lineName }}</span>
          </div>
        </template>
        <template v-else-if="item.prop === 'status'">
          <el-tag v-if="row.status === 1" type="success" size="small">
            {{ row.status | filterConst('CHANNEL_LINE_CONFIG_STATUS') }}
          </el-tag>
          <el-tag v-else type="danger" size="small">
            {{ (row.status === null || row.status === 2) ? '关闭' : (row.status | filterConst('CHANNEL_LINE_CONFIG_STATUS')) }}
          </el-tag>
        </template>
        <template v-else-if="item.prop === 'commissionType'">
          <el-tag v-if="row.status === 1" :type="row.commissionType === 1 ? 'primary' : 'success'" size="small">
            {{ row.commissionType | filterConst('CHANNEL_COMMISSION_TYPE') }}
          </el-tag>
          <span v-else class="text-muted">-</span>
        </template>
        <template v-else-if="item.prop === 'commissionValue'">
          <span v-if="row.status === 1" class="commission-value">
            {{ row.commissionValue }}{{ row.commissionType === 1 ? '元' : '%' }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template v-else-if="item.prop === 'commissionBearer'">
          <el-tag v-if="row.commissionBearer === 1" type="success" size="small">平台承担</el-tag>
          <el-tag v-else-if="row.commissionBearer === 2" type="warning" size="small">司机承担</el-tag>
          <span v-else class="text-muted">使用全局</span>
        </template>
        <template v-else>
          <span>{{ row[item.prop] || '-' }}</span>
        </template>
      </template>
    </custom-table>

    <!-- 编辑对话框 - 单个线路配置，使用 /api/v1/channel/lineConfig/save 接口 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="500px"
      :close-on-click-modal="false">
      <el-form :model="lineForm" :rules="rules" ref="lineForm" label-width="120px">
        <el-form-item label="线路名称">
          <span>{{ currentLine.lineName }}</span>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="lineForm.status">
            <el-radio
              v-for="item in lineConfigStatusOptions"
              :key="item.value"
              :label="item.value">
              {{ item.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="lineForm.status === 1">
          <el-form-item label="佣金类型" prop="commissionType">
            <el-radio-group v-model="lineForm.commissionType">
              <el-radio
                v-for="item in commissionTypeOptions"
                :key="item.value"
                :label="item.value">
                {{ item.label }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="佣金值" prop="commissionValue">
            <el-input-number
              v-model="lineForm.commissionValue"
              :min="0"
              :max="lineForm.commissionType === 1 ? 9999 : 100"
              :precision="2"
              :step="lineForm.commissionType === 1 ? 1 : 0.1">
            </el-input-number>
            <span style="margin-left: 10px;">{{ lineForm.commissionType === 1 ? '元' : '%' }}</span>
          </el-form-item>
          <el-form-item label="佣金承担方">
            <el-radio-group v-model="lineForm.commissionBearer">
              <el-radio :label="null">使用全局配置</el-radio>
              <el-radio :label="1">平台承担</el-radio>
              <el-radio :label="2">司机承担</el-radio>
            </el-radio-group>
          </el-form-item>
        </template>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saveLoading">保存</el-button>
      </span>
    </el-dialog>

    <!-- 批量设置对话框 - 批量配置多条线路，使用 /api/v1/channel/lineConfig/batchSave 接口 -->
    <el-dialog
      title="批量设置线路佣金"
      :visible.sync="batchDialogVisible"
      width="600px"
      :close-on-click-modal="false">
      <el-alert
        :title="`已选择 ${selectedLines.length} 条线路`"
        type="info"
        :closable="false"
        style="margin-bottom: 20px;">
      </el-alert>
      <el-form :model="batchForm" :rules="batchRules" ref="batchForm" label-width="120px" size="small">
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="batchForm.status">
            <el-radio
              v-for="item in lineConfigStatusOptions"
              :key="item.value"
              :label="item.value">
              {{ item.label }}
            </el-radio>
          </el-radio-group>
        </el-form-item>
        <template v-if="batchForm.status === 1">
          <el-form-item label="佣金类型" prop="commissionType">
            <el-radio-group v-model="batchForm.commissionType">
              <el-radio
                v-for="item in commissionTypeOptions"
                :key="item.value"
                :label="item.value">
                {{ item.label }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="佣金值" prop="commissionValue">
            <el-input-number
              v-model="batchForm.commissionValue"
              :min="0"
              :max="batchForm.commissionType === 1 ? 9999 : 100"
              :precision="2"
              :step="batchForm.commissionType === 1 ? 1 : 0.1">
            </el-input-number>
            <span style="margin-left: 10px;">{{ batchForm.commissionType === 1 ? '元' : '%' }}</span>
          </el-form-item>
          <el-form-item label="佣金承担方">
            <el-radio-group v-model="batchForm.commissionBearer">
              <el-radio :label="null">使用全局配置</el-radio>
              <el-radio :label="1">平台承担</el-radio>
              <el-radio :label="2">司机承担</el-radio>
            </el-radio-group>
          </el-form-item>
        </template>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="batchDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveBatch" :loading="batchLoading">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'LineConfig',
  data() {
    return {
      customOptions: {
        url: '/api/v1/channel/lineConfig/list',
        method: 'get',
        params: {},
        selection: true,
        search: [
          {
            type: 'input',
            field: 'keyword',
            name: '线路名称',
            value: ''
          },
          {
            type: 'select',
            field: 'status',
            name: '状态',
            value: '',
            option: this.$const.CHANNEL_LINE_CONFIG_STATUS || []
          }
        ]
      },
      columns: [
        { label: '线路名称', prop: 'lineName', minWidth: 180, align: 'left' },
        { label: '状态', prop: 'status', minWidth: 100, align: 'left' },
        { label: '佣金类型', prop: 'commissionType', minWidth: 100, align: 'left' },
        { label: '佣金值', prop: 'commissionValue', minWidth: 100, align: 'left' },
        { label: '承担方', prop: 'commissionBearer', minWidth: 100, align: 'left' }
      ],
      selectedLines: [],
      dialogVisible: false,
      dialogTitle: '配置线路佣金',
      currentLine: {},
      lineForm: {
        lineId: null,
        status: 1,
        commissionType: 1,
        commissionValue: 5,
        commissionBearer: null
      },
      rules: {
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ],
        commissionType: [
          { required: true, message: '请选择佣金类型', trigger: 'change' }
        ],
        commissionValue: [
          { required: true, message: '请输入佣金值', trigger: 'blur' }
        ]
      },
      saveLoading: false,
      batchDialogVisible: false,
      batchForm: {
        status: 1,
        commissionType: 2,
        commissionValue: 5,
        commissionBearer: null
      },
      batchRules: {
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ],
        commissionType: [
          { required: true, message: '请选择佣金类型', trigger: 'change' }
        ],
        commissionValue: [
          { required: true, message: '请输入佣金值', trigger: 'blur' }
        ]
      },
      batchLoading: false
    }
  },
  computed: {
    commissionTypeOptions() {
      return this.$const.CHANNEL_COMMISSION_TYPE || []
    },
    lineConfigStatusOptions() {
      return this.$const.CHANNEL_LINE_CONFIG_STATUS || []
    }
  },
  methods: {
    /**
     * 刷新列表
     * 作者：孙艺
     */
    refreshList() {
      this.$refs.customTable.getList()
    },
    /**
     * 选择变化
     * customTable 组件传递的是选中行的完整数据数组（通过 checkStatus 过滤）
     * 作者：孙艺
     */
    handleSelectionChange(val) {
      // val 是选中行的完整数据数组
      console.log(val,323232);
      this.selectedLines = val || []
    },
    /**
     * 编辑线路配置
     * 作者：孙艺
     */
    handleEdit(row) {
      this.currentLine = { ...row }
      this.lineForm = {
        lineId: row.lineId,
        status: row.status || 1,
        commissionType: row.commissionType || 1,
        commissionValue: row.commissionValue || 5,
        commissionBearer: row.commissionBearer || null
      }
      this.dialogTitle = `配置线路佣金 - ${row.lineName}`
      this.dialogVisible = true
    },
    /**
     * 保存线路配置（单个线路）
     * 使用接口：POST /api/v1/channel/lineConfig/save
     * 作者：孙艺
     */
    async handleSave() {
      this.$refs.lineForm.validate(async (valid) => {
        if (valid) {
          this.saveLoading = true
          try {
            const { lineId, status, commissionType, commissionValue, commissionBearer } = this.lineForm
            const res = await this.$axios.post('/api/v1/channel/lineConfig/save', {
              lineId,
              status,
              commissionType,
              commissionValue,
              commissionBearer
            })
            if (res && res.code === 1) {
              this.$message.success('保存成功')
              this.dialogVisible = false
              this.$refs.customTable.getList()
            } else {
              this.$message.error(res.msg || '保存失败')
            }
          } catch (error) {
            console.error('保存线路配置失败:', error)
            this.$message.error('保存失败')
          } finally {
            this.saveLoading = false
          }
        }
      })
    },
    /**
     * 批量设置
     * 作者：孙艺
     */
    handleBatchSet() {
      if (this.selectedLines.length === 0) {
        this.$message.warning('请先选择要配置的线路')
        return
      }
      this.batchForm = {
        status: 1,
        commissionType: 2,
        commissionValue: 5,
        commissionBearer: null
      }
      this.batchDialogVisible = true
    },
    /**
     * 批量保存线路配置（多条线路）
     * 使用接口：POST /api/v1/channel/lineConfig/batchSave
     * 参数格式：{ "configListJson": "[{...}, {...}]" }
     * 作者：孙艺
     */
    async handleSaveBatch() {
      this.$refs.batchForm.validate(async (valid) => {
        if (valid) {
          this.batchLoading = true
          try {
            const { status, commissionType, commissionValue, commissionBearer } = this.batchForm
            // 构建配置列表
            const configList = this.selectedLines.map(line => ({
              lineId: line.lineId,
              status,
              commissionType,
              commissionValue,
              commissionBearer
            }))
            // 将配置列表转换为 JSON 字符串
            const configListJson = JSON.stringify(configList)

            const res = await this.$axios.post('/api/v1/channel/lineConfig/batchSave', {
              configListJson
            })
            if (res && res.code === 1) {
              this.$message.success('批量设置成功')
              this.batchDialogVisible = false
              this.$refs.customTable.getList()
            } else {
              this.$message.error(res.msg || '批量保存失败')
            }
          } catch (error) {
            console.error('批量保存失败:', error)
            this.$message.error('批量保存失败')
          } finally {
            this.batchLoading = false
          }
        }
      })
    },
    /**
     * 获取状态类型（用于 el-tag）
     * 作者：孙艺
     * @param {Number} value - 状态值：1-开启，2-关闭
     * @returns {String} 状态类型
     */
    getStatusType(value) {
      const option = this.lineConfigStatusOptions.find(item => item.value === value)
      return option ? option.type : 'info'
    },
    /**
     * 刷新数据（供父组件调用）
     * 作者：孙艺
     */
    refresh() {
      this.refreshList()
    }
  }
}
</script>

<style lang="less" scoped>
// 容器与列表整体间距，与其它 Tab 保持一致
.lines-container {
  padding: 4px 0;
}

// 线路名称单元格
.line-name-cell {
  .line-name {
    display: block;
    font-weight: 500;
    color: #303133;
  }
}

// 佣金值
.commission-value {
  font-weight: 500;
  color: #409EFF;
}

// 文字颜色
.text-muted {
  color: #C0C4CC;
}
</style>

